import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { DietSkeleton } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  CardTitle,
  ContentMaxWidth,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/dietService'
import MealLogDialog from './components/MealLogDialog'
import { parseUtcDate } from '../../utils/dates'

const Slot = styled.button`
  appearance: none;
  border: 1px solid
    ${(p) => (p.$logged ? p.theme?.color?.accent : p.theme?.color?.border || '#E6E0D6')};
  background: ${(p) =>
    p.$logged ? p.theme?.color?.accentSoft || '#E1ECE7' : p.theme?.color?.bgRaised || '#fff'};
  border-radius: 14px;
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  &:hover { border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'}; }
`

const SlotLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-bottom: 4px;
`

const SlotValue = styled.div`
  font-size: 14px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  font-weight: ${(p) => (p.$logged ? 600 : 400)};
`

const Recommended = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const Grid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;
  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

export default function DietPage() {
  const { currentProfile } = usePatient()
  const [plan, setPlan] = useState(null)
  const [today, setToday] = useState(null)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [logDialog, setLogDialog] = useState(null)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const [p, t] = await Promise.all([
        svc.getPlan(currentProfile),
        svc.getToday(currentProfile),
      ])
      setPlan(p)
      setToday(t)
      const to = new Date().toISOString()
      const from = parseUtcDate(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      const h = await svc.listLog(currentProfile, from, to)
      setHistory(h)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setPlan(null)
    setToday(null)
    setHistory([])
    refresh()
  }, [refresh])

  if (loading && !today) {
    return (
      <ContentMaxWidth>
        <DietSkeleton />
      </ContentMaxWidth>
    )
  }

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/care">Back to Care</BackLink>
        <div>
          <Pill>Care</Pill>
          <PageHeading style={{ marginTop: 12 }}>Diet</PageHeading>
          <PageSubheading>
            {plan?.name ? plan.name : 'Daily meal log'}
            {plan?.prescribedBy && plan.prescribedBy !== 'self' ? ` · by ${plan.prescribedBy}` : ''}
          </PageSubheading>
        </div>

        {today && (
          <Card>
            <CardTitle style={{ marginBottom: 12 }}>Today</CardTitle>
            <Grid>
              {today.slots.map((s) => (
                <Slot
                  key={s.mealType}
                  $logged={s.logged}
                  onClick={() => setLogDialog(s)}
                >
                  <SlotLabel>{s.mealLabel}</SlotLabel>
                  <SlotValue $logged={s.logged}>
                    {s.logged ? s.summary || 'Logged' : 'Tap to log'}
                  </SlotValue>
                  {!s.logged && s.recommendedItems?.length > 0 && (
                    <Recommended>Try: {s.recommendedItems[0]}</Recommended>
                  )}
                </Slot>
              ))}
            </Grid>
            {today.hydrationGoalMl && (
              <Muted style={{ marginTop: 12 }}>
                Hydration goal: {today.hydrationGoalMl} ml
              </Muted>
            )}
          </Card>
        )}

        {plan && (
          <Card>
            <CardTitle style={{ marginBottom: 8 }}>Your plan</CardTitle>
            {plan.description && <Muted style={{ marginBottom: 8 }}>{plan.description}</Muted>}
            {plan.restrictions?.length > 0 && (
              <Muted>
                <strong>Avoid:</strong> {plan.restrictions.join(', ')}
              </Muted>
            )}
          </Card>
        )}

        <Card>
          <Row $between>
            <CardTitle style={{ margin: 0 }}>Last 7 days</CardTitle>
            <Pill>{history.length} entries</Pill>
          </Row>
          {!history.length ? (
            <Muted style={{ marginTop: 12 }}>
              Log meals on Today (or here) to start your history.
            </Muted>
          ) : (
            <div style={{ marginTop: 12 }}>
              {history.map((e) => (
                <div
                  key={e.id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: '1px dashed #E6E0D6',
                  }}
                >
                  <div style={{ width: 110, color: '#6B7280', fontSize: 13 }}>
                    {parseUtcDate(e.takenAt).toLocaleString([], {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>
                      {e.mealType.toLowerCase().replace(/^./, (c) => c.toUpperCase())}
                    </div>
                    <Muted>
                      {(e.items || []).join(', ')}
                      {e.quantity ? ` · ${e.quantity}` : ''}
                    </Muted>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </Stack>

      <MealLogDialog
        open={!!logDialog}
        mealType={logDialog?.mealType}
        mealLabel={logDialog?.mealLabel}
        onClose={() => setLogDialog(null)}
        onSubmit={async (body) => {
          await svc.log(currentProfile, body)
          await refresh()
        }}
      />
    </ContentMaxWidth>
  )
}
